/**
 * NavRow — shared presentational row used by NavItem and NavFold.
 *
 * Handles the common layout: icon | label | endAdornment (badge, chevron, etc.)
 * with condensed/expanded, active, and disabled states.
 */

import React from 'react';
import { css, cx } from '@emotion/css';
import { Theme, Typography, useTheme } from '@mui/material';

export interface NavRowProps {
  condensed?: boolean;
  icon?: React.ReactElement;
  iconSize?: number;
  label?: string;
  active?: boolean;
  disabled?: boolean;
  /** Rendered inside the icon wrapper (absolute positioned) — use for condensed badges */
  iconBadge?: React.ReactNode;
  /** Rendered at the end of the row (inline) — use for expanded badges, chevrons, etc. */
  endAdornment?: React.ReactNode;
  onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
  className?: string;
  id?: string;
}

const NavRow = React.forwardRef<HTMLDivElement, NavRowProps>(
  ({ condensed, icon, iconSize, label, active, disabled, iconBadge, endAdornment, onClick, className, id }, ref) => {
    const theme = useTheme();
    const cls = useNavRowStyles(theme, { condensed, iconSize, disabled });

    return (
      <div
        ref={ref}
        id={id}
        className={cx(cls.row, active && cls.rowActive, disabled && cls.rowDisabled, className)}
        onClick={disabled ? undefined : onClick}
        style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
      >
        <div className={cls.iconWrapper}>
          {icon &&
            React.cloneElement(icon as React.ReactElement<any>, {
              className: cls.icon,
            })}
          {iconBadge}
        </div>
        {!condensed && (
          <Typography className={cls.label}>{label || 'Feature'}</Typography>
        )}
        {endAdornment}
      </div>
    );
  },
);

NavRow.displayName = 'NavRow';

export const useNavRowStyles = (
  theme?: Theme,
  props?: { condensed?: boolean; iconSize?: number; disabled?: boolean },
) => {
  const effectiveIconSize = props?.iconSize || 20;

  return {
    row: css`
      position: relative;
      overflow: visible;
      white-space: nowrap;
      display: flex;
      flex-wrap: nowrap;
      align-items: center;
      justify-content: ${props?.condensed ? 'center' : 'flex-start'};
      width: 100%;
      padding: ${props?.condensed ? '8px' : '8px 12px'};
      border-radius: ${theme?.shape?.borderRadius || 6}px;
      margin-bottom: 4px;
      background-color: transparent;
      color: ${theme?.palette?.text?.contrastPrimary};
      cursor: pointer;
      transition: all 0.1s ease-in-out;
      &:hover,
      &:focus {
        background-color: ${theme?.palette?.action?.contrastHover};
      }
      &:active {
        background-color: ${theme?.palette?.action?.contrastSelected};
      }
    `,
    rowActive: css`
      background-color: rgba(255, 255, 255, 0.12);
      color: #ffffff;
      & > svg {
        color: #ffffff;
      }
      & .MuiTypography-root {
        color: #ffffff;
        font-weight: 600;
      }
    `,
    rowDisabled: css`
      opacity: 0.5;
      cursor: not-allowed !important;
      pointer-events: none;
      &:hover,
      &:focus {
        background-color: transparent !important;
      }
      & > svg {
        color: ${theme?.palette?.text?.contrastDisabled} !important;
      }
      & .MuiTypography-root {
        color: ${theme?.palette?.text?.contrastDisabled} !important;
      }
    `,
    iconWrapper: css`
      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-right: ${props?.condensed ? '0' : '12px'};
    `,
    icon: css`
      display: flex;
      align-items: center;
      justify-content: center;
      min-width: ${effectiveIconSize}px;
      min-height: ${effectiveIconSize}px;
      font-size: ${effectiveIconSize}px;
    `,
    label: css`
      flex-grow: 1;
      font-size: 13px !important;
      font-weight: 400 !important;
      line-height: 1.5;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    `,
  };
};

export default NavRow;
